import React from 'react'
// libs
import { reduxForm } from 'redux-form'
// components
import { FloatingActions } from 'components/FormElements'
import KPIScaleFormModal from 'components/Modals/ScaleFormModal'
import KPIResultTargetsModal from 'components/Modals/ResultTargetsModal'
import KPIRecurrenceFormModal from 'components/Modals/KPIRecurrenceModal'
import KPIIntegrationInfoModal from 'components/Modals/KPIIntegrationInfoModal'
// context
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { KPIContext, KPICreationContext } from 'context'
// constants
import { Forms, OBJECT_ENTITY_NAMES } from 'constants/index'
// hooks
import { useFieldAccess, useFormDirtyCount, useReviewFieldIds } from 'hooks'
// types
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// views
import { Collapse, Form } from 'components/Modals/PlanItems/styles'
// utils
import get from 'lodash/get'
import { generateFields } from 'utils/fields'

interface Props {
  onCancelClick: () => void
  isReadOnly?: boolean
}

const DetailsForm: React.FC<InjectedFormProps<any, Props> & Props> = ({
  form,
  reset,
  valid,
  handleSubmit,
  onCancelClick,
  isReadOnly
}) => {
  const [isCollapsed, setExpanded] = React.useState(false)

  const { plan, entity, handleDetailsSubmit } = React.useContext<any>(KPIContext)
  const { fields, isCreateForm } = React.useContext<any>(KPICreationContext)
  const { isLinkingMode } = React.useContext(LinkingModeContext)

  const canViewRestricted = useFieldAccess(entity)
  const reviewFieldIds = useReviewFieldIds(OBJECT_ENTITY_NAMES.KPI)

  const { dirtyFieldsCount } = useFormDirtyCount(form)

  const { kpiModifyKPI, kpiModifyUserKPISecurity } = get(entity, 'currentPermissions', {})

  React.useEffect(() => {
    if (isLinkingMode) {
      setExpanded(true)
    }
  }, [isLinkingMode])

  return (
    <>
      <Collapse isCollapsed={isCollapsed} handleClick={() => setExpanded(!isCollapsed)}>
        About this indicator
      </Collapse>
      {!isCollapsed && (
        <Form onSubmit={handleSubmit}>
          {(fields || entity.fields)
            .filter((field) => !reviewFieldIds.includes(field.id))
            .map((field) =>
              generateFields({
                field,
                form,
                canViewRestricted,
                canModify: form === Forms.KPI_CREATE_FORM || (!isReadOnly && !plan.locked && kpiModifyKPI),
                canSetPermissions: form === Forms.KPI_CREATE_FORM || (!isReadOnly && kpiModifyUserKPISecurity)
              })
            )}
          <FloatingActions
            form={form}
            valid={valid}
            isCreateForm={isCreateForm}
            dirtyCount={dirtyFieldsCount}
            handleConfirm={handleDetailsSubmit}
            onCancel={isCreateForm ? onCancelClick : reset}
          />
        </Form>
      )}

      <KPIRecurrenceFormModal displayField={'recurrenceText'} targetField={'recurrence'} targetForm={form} />
      <KPIScaleFormModal displayField={'resultTypeScaleText'} targetField={'resultTypeScale'} targetForm={form} />
      <KPIResultTargetsModal displayField={'resultTargetText'} targetField={'resultTarget'} targetForm={form} />
      <KPIIntegrationInfoModal entity={entity} />
    </>
  )
}

export default reduxForm<any, Props>({
  destroyOnUnmount: false,
  enableReinitialize: true
})(DetailsForm)
